import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import RichTextEditor from "./components/RichTextEditor";

type NewsCategory = {
  id: number;
  name: string;
  slug: string;
};

type NewsItem = {
  id: number;
  slug: string;
  title: string;
  description: string;
  created_by: string | null;
  cover_image: string | null;
  published: boolean;
  category_id: number | null;
};

export default function AdminNewsEdit() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [categories, setCategories] = React.useState<NewsCategory[]>([]);
  const [item, setItem] = React.useState<NewsItem | null>(null);
  const [form, setForm] = React.useState({
    slug: "",
    title: "",
    description: "",
    created_by: "",
    cover_image: "",
    published: true,
    category_id: "" as string | "",
  });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const [catRes, newsRes] = await Promise.all([
        fetch("/api/admin/news-categories"),
        slug ? fetch(`/api/admin/news/${slug}`) : Promise.resolve(null),
      ]);
      const catData = (await catRes.json()) as { status: string; data: NewsCategory[] };
      setCategories(catData.data || []);
      if (newsRes) {
        const newsJson = (await newsRes.json()) as { status: string; data: NewsItem | null };
        if (newsJson.status === "ok" && newsJson.data) {
          const n = newsJson.data;
          setItem(n);
          setForm({
            slug: n.slug,
            title: n.title,
            description: n.description,
            created_by: n.created_by ?? "",
            cover_image: n.cover_image ?? "",
            published: n.published,
            category_id: n.category_id ? String(n.category_id) : "",
          });
        }
      }
    })();
  }, [slug]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!item) return;
    setSaving(true);
    await fetch(`/api/admin/news/${item.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug: form.slug,
        title: form.title,
        description: form.description,
        created_by: form.created_by || null,
        cover_image: form.cover_image || null,
        published: form.published,
        category_id: form.category_id ? Number(form.category_id) : null,
      }),
    });
    setSaving(false);
    nav("/admin/news");
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/admin/upload", { method: "POST", body: fd });
    const data = (await res.json()) as { status: string; url?: string };
    if (data.status === "ok" && data.url) {
      setForm((prev) => ({ ...prev, cover_image: data.url! }));
    }
  }

  if (!item) return <p className="text-sm text-foreground/70">กำลังโหลด...</p>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">แก้ไขข่าว</h1>
        <Button asChild variant="outline">
          <Link to="/admin/news">กลับไปหน้ารายการ</Link>
        </Button>
      </div>
      <Card className="p-4 bg-card/60">
        <form onSubmit={save} className="grid gap-3">
          <div className="grid gap-1">
            <Label>Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-1">
            <Label>ชื่อข่าว</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-1">
            <Label>ผู้เขียน</Label>
            <Input
              value={form.created_by}
              onChange={(e) => setForm({ ...form, created_by: e.target.value })}
              placeholder="เช่น ทีมงาน Lucky News"
            />
          </div>
          <div className="grid gap-1">
            <Label>หมวดหมู่</Label>
            <Select
              value={form.category_id}
              onValueChange={(v) => setForm({ ...form, category_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="เลือกหมวดหมู่" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">ไม่ระบุหมวดหมู่</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>รูปปกข่าว</Label>
            <Input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleUpload} />
            {form.cover_image && (
              <img
                src={form.cover_image}
                alt="cover"
                className="mt-2 h-32 w-full object-cover rounded-md border border-border/60"
              />
            )}
          </div>
          <div className="grid gap-1">
            <Label>เนื้อหาข่าว (คำอธิบายยาว)</Label>
            <RichTextEditor
              value={form.description}
              onChange={(html) => setForm({ ...form, description: html })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="pub"
              checked={form.published}
              onCheckedChange={(v) => setForm({ ...form, published: Boolean(v) })}
            />
            <Label htmlFor="pub">เผยแพร่</Label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? "กำลังบันทึก..." : "บันทึกการเปลี่ยนแปลง"}
            </Button>
            <Button type="button" variant="outline" onClick={() => nav("/admin/news")}>
              ยกเลิก
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
